import { useRef, useState } from 'react';
import { productIdPathParams } from '@/lib/product-url-codec';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DispatchShell } from '@/components/DispatchShell';
import { PageHero } from '@/components/layout/PageHero';
import { useProducts } from '@/features/product/hooks';
import { notifySupportingDocsRefusal } from '@/features/product/dispatch-gate';
import type { Product } from '@/features/product/types';
import { countryLabel } from '@/features/agent-dispatch/countryLabel';
import { finderKindFor, pathwayInfoFor } from '@/features/predicate/pathway';
import {
  classificationGapFromError,
  existingRunIdFromConflict,
  lockedCountriesFromConflict,
  type FinderKind,
} from '@/features/predicate/api';
import {
  useDispatchFinderRun,
  usePredicateAvailability,
} from '@/features/predicate/hooks';
import { PredicateMarketPicker } from '@/features/predicate/components/PredicateMarketPicker';
import { finderRefusalToast } from '@/features/predicate/finder-refusal';
import {
  DRUG_FINDER_GENERATE_LABEL,
  DRUG_DOSSIER_COPY,
  DRUG_FINDER_HERO,
  DRUG_RESULT_COPY,
  isDrugFinderKind,
} from '@/features/predicate/components/drug/copy';
import { useIsAdmin } from '@/features/auth/hooks';
import { RunLoadingSkeleton } from '@/components/RunLoadingSkeleton';
import { notifyAlreadyRunning } from '@/features/agent-dispatch/alreadyRunning';

/**
 * Predicate Finder — per-product detail page.
 *
 * Reads `productId` from the URL path. Fetches the product (from the shared
 * products query — same fetch the list page uses, so it hits the cache when
 * the user navigates from the list).
 *
 * Renders a Subject Device card with:
 *   - Product name + type + meta (Product ID, Submitted, Last Analyzed)
 *   - "No finder applies" notice if the pathway has no finder
 *   - Run Analysis button (3 states: Run / View Running / Starting…)
 *   - Download Latest Report link if a completed run exists
 *
 * 409 in-flight conflict → navigates to the in-progress run's result page.
 */
export const Route = createFileRoute('/predicate-finder_/$productId')({
  // Opaque token in the address bar, never the global product id.
  params: productIdPathParams,
  component: PredicateFinderDetailPage,
});

interface FinderConfig {
  /** Submit-button label. ``Generate <noun>`` for visual parity with the
   * other dispatch forms (Clinical Protocol / Report / Dossier / Strategy). */
  generateLabel: string;
  /** What the finder produces, in the "already up to date" copy. */
  analysisName: string;
  inFlightIdKey: keyof Product;
  latestIdKey: keyof Product;
}

// The drug/biologic finders run on the predicate run table, so they share
// its in-flight / latest-run fields.
const FINDER_CONFIG: Record<FinderKind, FinderConfig> = {
  predicate: {
    generateLabel: 'Generate Predicates',
    analysisName: 'predicate analysis',
    inFlightIdKey: 'in_flight_predicate_run_id',
    latestIdKey: 'latest_predicate_run_id',
  },
  generic: {
    generateLabel: DRUG_FINDER_GENERATE_LABEL.generic,
    analysisName: DRUG_RESULT_COPY.staleAnalysisName.generic,
    inFlightIdKey: 'in_flight_predicate_run_id',
    latestIdKey: 'latest_predicate_run_id',
  },
  reference: {
    generateLabel: DRUG_FINDER_GENERATE_LABEL.reference,
    analysisName: DRUG_RESULT_COPY.staleAnalysisName.reference,
    inFlightIdKey: 'in_flight_predicate_run_id',
    latestIdKey: 'latest_predicate_run_id',
  },
};

function PredicateFinderDetailPage() {
  const { productId } = Route.useParams();
  const { data: products, isLoading, isError } = useProducts();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useDispatchFinderRun();
  const isAdmin = useIsAdmin();
  const [country, setCountry] = useState<string[]>([]);
  const { data: availability } = usePredicateAvailability(Number(productId));
  // Set synchronously on the first click: `dispatch.isPending` only disables the
  // button after React re-renders, and a fast double-click lands before that.
  const dispatchInFlight = useRef(false);

  if (isLoading) return <DetailPageShell><LoadingCard /></DetailPageShell>;
  if (isError) return <DetailPageShell><NotFoundCard /></DetailPageShell>;

  const product = (products ?? []).find((p) => String(p.id) === productId);
  if (!product) return <DetailPageShell><NotFoundCard /></DetailPageShell>;

  const kind = finderKindFor(product);
  const cfg = kind ? FINDER_CONFIG[kind] : null;
  const applies = kind !== null && cfg !== null;
  const inFlightId = cfg ? (product[cfg.inFlightIdKey] as number | null) : null;
  const pathwayNote = !applies ? pathwayInfoFor(product.product_type) : null;

  // Only a LOADED, decided availability can produce the dead end — pending
  // checks fail open. Mirrors the strategy dispatch form.
  const allMarketsCurrent =
    availability != null
    && availability.markets.length > 0
    && availability.markets.every((m) => !m.available);

  // Every product edits on its product page, as in the product table.
  const editParams = { productId: product.id };

  async function handleRun() {
    if (!applies || !kind || !cfg || dispatchInFlight.current) return;
    dispatchInFlight.current = true;
    try {
      const response = await dispatch.mutateAsync({
        kind,
        productId: product!.id,
        country,
      });
      // Prefer dispatch_group_id (new multi-country shape, always present
      // after migration 0023). Fall back to run_id for safety; the result
      // page still handles legacy single-run URLs.
      if (response.dispatch_group_id) {
        void navigate({
          to: '/predicate-finder-result',
          search: { dispatch_group_id: response.dispatch_group_id },
        });
      } else {
        void navigate({
          to: '/predicate-finder-result',
          search: { run_id: response.run_id },
        });
      }
    } catch (err) {
      const existing = existingRunIdFromConflict(err);
      if (existing) {
        notifyAlreadyRunning();
        void navigate({ to: '/predicate-finder-result', search: { run_id: existing } });
        return;
      }
      if (
        notifySupportingDocsRefusal(err, (pid) =>
          void navigate({ to: '/product-account/$productId/edit', params: { productId: pid } }),
        )
      ) {
        return;
      }
      // The re-run gate's 409 carries `locked_countries`, not
      // `existing_run_id`: name the rejected markets and refetch so the
      // picker corrects itself.
      const locked = lockedCountriesFromConflict(err);
      if (locked) {
        const names = locked.map((c) => countryLabel(c)).join(', ');
        toast.error(`${names}: already up to date, so this could not run.`);
        void queryClient.invalidateQueries({
          queryKey: ['predicate-availability', product!.id],
        });
        return;
      }
      const gap = classificationGapFromError(err);
      if (gap) {
        const refusal = finderRefusalToast(product!.product_type, gap);
        toast.error(refusal.title, {
          description: refusal.description ?? undefined,
          duration: 12000,
          action: {
            label: 'Set classification',
            onClick: () => {
              void navigate({ to: '/product-account/$productId/edit', params: editParams });
            },
          },
        });
        return;
      }
      toast.error(
        err instanceof Error ? err.message : 'Could not start the analysis. Please try again.',
      );
    } finally {
      dispatchInFlight.current = false;
    }
  }

  const status = !applies
    ? "This pathway isn’t available for this product type."
    : inFlightId
      ? 'A run is already in progress.'
      : allMarketsCurrent
        ? `Every market already has a ${cfg?.analysisName ?? 'predicate analysis'} built from the current product details.`
        : country.length === 0
          ? 'Pick at least one country.'
          : dispatch.isPending
            ? 'Starting the run…'
            : 'Ready to generate.';

  const primaryAction = !applies ? (
    <Button variant="brand" disabled>
      <Play className="size-3.5" /> {cfg?.generateLabel ?? 'Generate'}
    </Button>
  ) : inFlightId ? (
    <Button
      variant="brand"
      type="button"
      onClick={() => {
        void navigate({
          to: '/predicate-finder-result',
          search: { run_id: inFlightId },
        });
      }}
    >
      <Play className="size-3.5" /> View Running Analysis
    </Button>
  ) : (
    <Button
      variant="brand"
      type="submit"
      disabled={dispatch.isPending || !isAdmin || country.length === 0}
      title={
        !isAdmin
          ? 'Admin only'
          : allMarketsCurrent
            ? `Every market already has an up-to-date ${cfg?.analysisName ?? 'predicate analysis'}`
            : undefined
      }
    >
      <Play className="size-3.5" />
      {dispatch.isPending ? 'Starting…' : (cfg?.generateLabel ?? 'Generate')}
    </Button>
  );

  return (
    <DetailPageShell kind={kind}>
      <DispatchShell
        product={product}
        onSubmit={(e) => {
          e.preventDefault();
          if (applies && !inFlightId) void handleRun();
        }}
        status={status}
        primaryAction={primaryAction}
      >
        {pathwayNote && (
          <div className="bg-state-info-bg border-l-4 border-state-info-accent rounded-md px-4 py-3">
            <div className="text-sm font-semibold text-state-info-text mb-0.5">
              {pathwayNote.title}
            </div>
            <div className="text-sm text-state-info-text/90">{pathwayNote.detail}</div>
          </div>
        )}

        {applies && !inFlightId && (
          // Markets come from the predicate availability endpoint; locked
          // markets group separately with report links, like the strategy picker.
          <PredicateMarketPicker
            productId={product.id}
            productType={product.product_type}
            kind={kind}
            value={country}
            onChange={setCountry}
          />
        )}
        {applies && !inFlightId && allMarketsCurrent && (
          // All-locked dead end: offer a way to make a change (Edit, routed
          // by shape) and a way to leave without one.
          <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-line bg-line-soft px-4 py-3">
            <p className="m-0 text-[12.5px] text-muted">
              Every market already has an up-to-date {cfg?.analysisName ?? 'predicate analysis'} for{' '}
              <span className="font-semibold text-navy">
                {product.product_name?.trim() || 'this product'}
              </span>
              . Edit the product to reopen a market, or view the analyses above.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" asChild>
                <Link to="/product-account/$productId/edit" params={editParams}>
                  Edit product
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/predicate-finder">Back to product list</Link>
              </Button>
            </div>
          </div>
        )}
        {kind === 'predicate' && !inFlightId && (
          <p className="text-[12.5px] text-muted leading-[1.55] m-0">
            After the run, review the ranked candidates and choose your
            primary match — your submission dossier anchors its
            equivalence case on your pick. Until you choose, the
            finder&rsquo;s top match is used by default.
          </p>
        )}
        {kind && isDrugFinderKind(kind) && !inFlightId && (
          <p className="text-[12.5px] text-muted leading-[1.55] m-0">
            {DRUG_DOSSIER_COPY[kind].finderPageNote}
          </p>
        )}
      </DispatchShell>
    </DetailPageShell>
  );
}

function DetailPageShell({
  kind = null,
  children,
}: {
  kind?: FinderKind | null;
  children: React.ReactNode;
}) {
  const drugHero = isDrugFinderKind(kind) ? DRUG_FINDER_HERO[kind] : null;
  return (
    <>
      <PageHero
        title={drugHero?.title ?? 'Existing Products'}
        subtitle={
          drugHero?.subtitle
          ?? 'Scored device matches for your submission — US FDA 510(k) predicates (grounded in openFDA) and EU MDR equivalent devices / clinical comparators (grounded in EUDAMED) via the REGARA AI agent.'
        }
      />
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20">{children}</main>
    </>
  );
}

function LoadingCard() {
  return (
    <RunLoadingSkeleton label="Loading product" />
  );
}

function NotFoundCard() {
  return (
    <div className="bg-white border border-line rounded-2xl p-12 text-center shadow-card">
      <p className="text-muted m-0 mb-3">
        Product not found, or you do not have access to it.
      </p>
      <a
        href="/predicate-finder"
        className="text-navy font-semibold border-b border-line-strong hover:border-electric pb-0.5"
      >
        ← Back to predicate finder
      </a>
    </div>
  );
}

